import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { AlertController } from '@ionic/angular/standalone';
import { API_BASE_URL } from '@postal-bridge/shared/api';
import { firstValueFrom } from 'rxjs';

export interface InstalledAppInfo {
  version: string;
  build: number;
  platform: string;
  native: boolean;
}

export interface MobileReleasePolicy {
  app: string;
  platform: string;
  latestVersion: string;
  latestBuild: number;
  minimumSupportedVersion: string;
  minimumSupportedBuild: number;
  storeUrl: string;
}

export type MobileUpdateLevel = 'none' | 'recommended' | 'required';

export function evaluateMobileRelease(currentBuild: number, policy: MobileReleasePolicy): MobileUpdateLevel {
  if (!Number.isFinite(currentBuild) || currentBuild <= 0) return 'none';
  if (currentBuild < policy.minimumSupportedBuild) return 'required';
  return currentBuild < policy.latestBuild ? 'recommended' : 'none';
}

@Injectable({ providedIn: 'root' })
export class MobileUpdateService {
  private readonly http = inject(HttpClient);
  private readonly alerts = inject(AlertController);
  private readonly apiBase = String(inject(API_BASE_URL)).replace(/\/+$/, '');
  private checkPromise?: Promise<void>;

  async getInstalledAppInfo(): Promise<InstalledAppInfo> {
    const platform = Capacitor.getPlatform();
    if (!Capacitor.isNativePlatform()) {
      return { version: 'Web', build: 0, platform, native: false };
    }

    try {
      const info = await App.getInfo();
      return {
        version: String(info.version || 'Unknown'),
        build: Number.parseInt(String(info.build || '0'), 10) || 0,
        platform,
        native: true,
      };
    } catch {
      return { version: 'Unknown', build: 0, platform, native: true };
    }
  }

  checkForUpdates(): Promise<void> {
    return this.checkPromise ??= this.checkForUpdatesCore();
  }

  private async checkForUpdatesCore(): Promise<void> {
    const installed = await this.getInstalledAppInfo();
    if (!installed.native || installed.platform !== 'android' || installed.build <= 0) return;

    try {
      const policy = await firstValueFrom(this.http.get<MobileReleasePolicy>(
        `${this.apiBase}/config/mobile-release`,
        { params: { app: 'customer', platform: installed.platform } },
      ));
      const level = evaluateMobileRelease(installed.build, policy);
      if (level === 'none') return;

      const dismissalKey = `pb.mobileUpdate.dismissed.customer.${installed.platform}.${policy.latestBuild}`;
      if (level === 'recommended' && localStorage.getItem(dismissalKey) === '1') return;

      const required = level === 'required';
      const alert = await this.alerts.create({
        header: required ? 'Update required' : 'PostalBridge update available',
        message: required
          ? `Version ${policy.latestVersion} is required to continue receiving fixes and securely access your mailbox.`
          : `Version ${policy.latestVersion} is available with the latest reliability and security improvements.`,
        backdropDismiss: !required,
        buttons: required
          ? [{
              text: 'Update now',
              handler: () => {
                void Browser.open({ url: policy.storeUrl });
                return false;
              },
            }]
          : [
              {
                text: 'Later',
                role: 'cancel',
                handler: () => localStorage.setItem(dismissalKey, '1'),
              },
              {
                text: 'Update',
                handler: () => void Browser.open({ url: policy.storeUrl }),
              },
            ],
      });
      await alert.present();
    } catch {
      // Update checks must never prevent the app from opening.
    }
  }
}
